import { useEffect, useState } from 'react';
import { FloppyDisk, ArrowCounterClockwise, CaretDown, Lock, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';
import type { PromptResponse } from '../../../../services/dashboard.service';
import { promptApi, type AiSettingsKind } from './promptApi';

const formatTimestamp = (iso: string | null): string => {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export const PromptTab = ({ kind = 'project' }: { kind?: AiSettingsKind }) => {
  const api = promptApi(kind);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [template, setTemplate] = useState('');
  const [savedTemplate, setSavedTemplate] = useState('');
  const [rendered, setRendered] = useState('');
  const [markers, setMarkers] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);

  const applyResponse = (data: PromptResponse) => {
    setTemplate(data.template);
    setSavedTemplate(data.template);
    setRendered(data.rendered);
    setMarkers(data.markers);
    setUpdatedAt(data.updated_at);
    setUpdatedBy(data.updated_by);
    setIsDefault(data.is_default);
  };

  const load = async () => {
    setLoading(true);
    try {
      applyResponse(await api.getPrompt());
    } catch (err: any) {
      toast.error(err.message || 'Failed to load the prompt.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const dirty = template !== savedTemplate;

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.savePrompt(template);
      applyResponse(data);
      toast.success('Prompt saved.');
    } catch (err: any) {
      // The backend names the missing marker in `detail` — show it as-is.
      toast.error(err.message || 'Failed to save the prompt.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async () => {
    if (isDefault) return;
    if (!window.confirm('Restore the built-in default prompt? Your edits will be discarded.')) return;
    setRestoring(true);
    try {
      const data = await api.restorePrompt();
      applyResponse(data);
      toast.success('Restored the default prompt.');
    } catch (err: any) {
      toast.error(err.message || 'Failed to restore the default prompt.');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex justify-center items-center h-48 text-slate-400 text-sm">Loading prompt…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Editor ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-sm font-bold text-slate-800">{kind === 'proposal' ? 'Proposal form prompt' : 'Classification prompt'}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRestore}
              disabled={restoring || saving || isDefault}
              title={isDefault ? 'Already the default prompt' : 'Restore the built-in default'}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 font-bold rounded-xl text-sm border border-slate-200 transition-smooth"
            >
              <ArrowCounterClockwise size={16} weight="bold" />
              {restoring ? 'Restoring…' : 'Restore default'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || restoring || !dirty}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl text-sm transition-smooth"
            >
              <FloppyDisk size={16} weight="bold" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        {/* Markers explainer */}
        <div className="mb-4 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {markers.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-[11px] font-mono font-bold text-slate-700"
              >
                <Lock size={11} className="text-slate-400" weight="bold" />
                {m}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 font-medium leading-relaxed flex items-start gap-1.5">
            <Info size={14} className="text-accent shrink-0 mt-0.5" />
            These are filled in automatically —{' '}
            {kind === 'proposal' ? (
              <><code className="font-mono">{'{{FIELD_OPTIONS}}'}</code> with the domain, sub-domain, department and SDG lists,</>
            ) : (
              <><code className="font-mono">{'{{ALLOWED_CATEGORIES}}'}</code> with the category list,</>
            )}{' '}
            <code className="font-mono">{'{{LEARNED_RULES}}'}</code> with what the AI has
            learned from corrections. Removing one is rejected.
          </p>
        </div>

        <textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={24}
          spellCheck={false}
          className="w-full bg-slate-50/60 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 rounded-xl px-4 py-3 text-xs font-mono text-slate-800 outline-none transition-smooth resize-y"
        />

        <p className="mt-3 text-[11px] text-slate-400 font-medium">
          Last updated {formatTimestamp(updatedAt)}
          {updatedBy ? ` by ${updatedBy}` : ''}.
        </p>
      </div>

      {/* ── Preview ── */}
      <details className="bg-white rounded-2xl shadow-sm border border-slate-100 group [&_summary::-webkit-details-marker]:hidden">
        <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
          <span className="text-sm font-bold text-slate-800">Preview — what the AI actually receives</span>
          <CaretDown size={16} weight="bold" className="text-slate-400 group-open:rotate-180 transition-transform duration-200" />
        </summary>
        <div className="px-6 pb-6 border-t border-slate-100 pt-4">
          <pre className="whitespace-pre-wrap break-words bg-slate-50/60 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono text-slate-700 max-h-[32rem] overflow-y-auto">
            {rendered}
          </pre>
        </div>
      </details>
    </div>
  );
};
